import React, { useCallback, useMemo, useState } from 'react';
import { Button, addToast } from '@heroui/react';
import { CalendarClock, Check, ClipboardCheck } from 'lucide-react';
import EndorsementCard from './components/EndorsementCard.js';
import CredentialSection from './components/CredentialSection.js';
import { AppDatePicker } from '../resources/components/AppDatePicker.js';
import {
	ENDORSEMENT_TYPES,
	saveEndorsements,
	saveFlightReview,
	deleteEndorsementAsset,
	uploadEndorsementAsset
} from './endorsementsApi.js';
import { toPickerValue, toApiDate, expiryState, faaReviewExpiry } from './documentDates.js';
import { formatUserDate } from './formatUserDate.js';
import CredentialsApi, { apiErrorText } from './credentialsApi.js';
import { uploadKindOf, uploadEach, uploadSummary } from './attachments.js';
import AttachmentList from './components/AttachmentList.js';
import useSectionRefresh from './useSectionRefresh.js';

/**
 * EndorsementsPage — `/users/personalinfo/`, "Endorsements & Flight Reviews".
 *
 * Reads the payload the twig injects (the four endorsement records, the list
 * of ticked keys, and the FAA review) and writes through `endorsementsApi`.
 *
 * Endorsement ticks save immediately, as the legacy page did — it watched the
 * checkbox list and posted on every change. The flight review has an explicit
 * Save because it is two dates, and autosaving half-entered dates is worse
 * than a button.
 */
function EndorsementsPage({ payload, onRefresh }) {
	const records = useMemo(() => {
		const out = {};
		ENDORSEMENT_TYPES.forEach(t => {
			out[t.key] =
				payload[t.key] && typeof payload[t.key] === 'object'
					? payload[t.key]
					: { attachments: [] };
		});
		return out;
	}, [payload]);

	const faaReview = useMemo(() => {
		const r = payload.faaReview;
		return r && typeof r === 'object' ? r : {};
	}, [payload]);

	const [checked, setChecked] = useState(() => {
		const raw = payload.endorsements;
		const list = Array.isArray(raw) ? raw : [];
		return list.filter(v => typeof v === 'string');
	});
	const [busy, setBusy] = useState(false);
	const [reviewDate, setReviewDate] = useState(() => toPickerValue(faaReview.date));
	const [savingReview, setSavingReview] = useState(false);
	const refresh = useSectionRefresh(onRefresh);

	const handleToggle = useCallback(
		async key => {
			const next = checked.includes(key) ? checked.filter(k => k !== key) : [...checked, key];
			const previous = checked;
			setChecked(next);
			setBusy(true);
			try {
				await saveEndorsements(next);
			} catch (e) {
				setChecked(previous);
				addToast({
					title: 'Could not save endorsements',
					description: apiErrorText(e),
					color: 'danger'
				});
			} finally {
				setBusy(false);
			}
		},
		[checked]
	);

	const handleDeleteAsset = useCallback(
		async (typeKey, asset) => {
			const record = records[typeKey] || {};
			// eslint-disable-next-line no-alert
			if (!window.confirm('Remove this attachment?')) return;
			setBusy(true);
			try {
				await deleteEndorsementAsset(asset.kind, typeKey);
				await refresh();
				setBusy(false);
				addToast({ title: 'Scan removed', color: 'success' });
			} catch (e) {
				setBusy(false);
				addToast({
					title: 'Could not remove the attachment',
					description: apiErrorText(e),
					color: 'danger'
				});
			}
		},
		[records, refresh]
	);

	const handleUploadAsset = useCallback(async (typeKey, file) => {
		setBusy(true);
		try {
			await uploadEndorsementAsset(typeKey, file);
			await refresh();
			setBusy(false);
			addToast({ title: 'Scan attached', color: 'success' });
		} catch (e) {
			setBusy(false);
			addToast({ title: 'Upload failed', description: apiErrorText(e), color: 'danger' });
		}
	}, [refresh]);

	// The review's scans hang off its id; adding/removing one touches no dates.
	const handleUploadReviewAsset = useCallback(
		async files => {
			setSavingReview(true);
			const result = await uploadEach(files, file =>
				CredentialsApi.uploadFlightReviewAsset(faaReview.id, file, uploadKindOf(file))
			);
			if (result.done) await refresh();
			setSavingReview(false);
			addToast(uploadSummary(result, apiErrorText));
		},
		[faaReview.id, refresh]
	);

	const handleDeleteReviewAsset = useCallback(
		async item => {
			// eslint-disable-next-line no-alert
			if (!window.confirm(`Remove ${item.name}?`)) return;
			setSavingReview(true);
			try {
				await CredentialsApi.removeFlightReviewAsset(faaReview.id, item.kind, item.id);
				await refresh();
				setSavingReview(false);
				addToast({ title: 'Scan removed', color: 'success' });
			} catch (e) {
				setSavingReview(false);
				addToast({ title: 'Could not remove the attachment', description: apiErrorText(e), color: 'danger' });
			}
		},
		[faaReview.id, refresh]
	);

	const handleSaveReview = useCallback(async () => {
		setSavingReview(true);
		try {
			await saveFlightReview({
				reviewId: faaReview.id,
				date: toApiDate(reviewDate)
			});
			await refresh();
			setSavingReview(false);
			addToast({ title: 'Flight review saved', color: 'success' });
		} catch (e) {
			setSavingReview(false);
			addToast({
				title: 'Could not save the flight review',
				description: apiErrorText(e),
				color: 'danger'
			});
		}
	}, [faaReview.id, reviewDate, refresh]);

	const exp = expiryState(faaReview.expirationDate);

	return (
		<>
			<CredentialSection
				id="endorsements"
				icon={ClipboardCheck}
				title="Endorsements"
				description="Tick an endorsement, then attach the signed logbook page. Changes save as you tick."
				count={checked.length}
			>
				<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
					{ENDORSEMENT_TYPES.map(type => (
						<EndorsementCard
							key={type.key}
							type={type}
							checked={checked.includes(type.key)}
							record={records[type.key]}
							onToggle={handleToggle}
							onDeleteAsset={handleDeleteAsset}
							onUploadAsset={handleUploadAsset}
							busy={busy}
						/>
					))}
				</div>
			</CredentialSection>

			<CredentialSection
				id="flight-review"
				icon={CalendarClock}
				title="FAA Flight Review"
				description="Recorded every 24 calendar months."
			>
				<div className="flex flex-col gap-4">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<AppDatePicker
							label="Last flight review"
							variant="bordered"
							labelPlacement="outside"
							radius="sm"
							granularity="day"
							value={reviewDate}
							onChange={setReviewDate}
						/>
						{/* Not an input: FAA expiry follows the review date (server and legacy
						    derive it), so it updates as the date changes. */}
						<div className="flex flex-col gap-1.5">
							<span className="text-small text-foreground">Expires</span>
							<div className="h-10 flex items-center rounded-small bg-default-100 px-3 text-[14px] text-foreground">
								{formatUserDate(faaReviewExpiry(toApiDate(reviewDate)), '—')}
							</div>
							<span className="text-[12px] text-default-400">
								24 calendar months after the review, to the end of that month.
							</span>
						</div>
					</div>

					{exp && (
						<div
							className={[
								'flex items-center gap-2 rounded-lg px-3 py-2.5',
								exp.tone === 'danger'
									? 'bg-danger-50 text-danger-700'
									: exp.tone === 'warning'
										? 'bg-warning-100 text-warning-700'
										: 'bg-success-50 text-success-700'
							].join(' ')}
						>
							<CalendarClock className="w-4 h-4 shrink-0" aria-hidden="true" />
							<span className="text-[13px]">
								{exp.label} — currently expires {formatUserDate(faaReview.expirationDate)}.
							</span>
						</div>
					)}

					<div className="flex flex-col gap-2">
						<span className="text-[13px] font-medium text-default-700">Logbook scans</span>
						{faaReview.id ? (
							<AttachmentList
								items={faaReview.attachments}
								multiple
								policy="pdf"
								onUpload={handleUploadReviewAsset}
								onDelete={handleDeleteReviewAsset}
								uploading={savingReview}
							/>
						) : (
							<p className="text-[12.5px] text-default-500 m-0">
								Save the flight review first — scans can be attached to it afterwards.
							</p>
						)}
					</div>

					<div className="flex flex-wrap items-center justify-end gap-3">
						<Button
							color="primary"
							radius="sm"
							isLoading={savingReview}
							startContent={savingReview ? null : <Check className="w-4 h-4" />}
							onPress={handleSaveReview}
						>
							Save Flight Review
						</Button>
					</div>
				</div>
			</CredentialSection>
		</>
	);
}

export default EndorsementsPage;
